/**
 * Toggle Component
 *
 * A universal iOS-style toggle switch component for boolean settings.
 * Uses theme colors and consistent sizing across the app.
 */

import type { CSSProperties } from 'react';

/**
 * Toggle dimensions (iOS-style)
 */
const TOGGLE_WIDTH = 51;
const TOGGLE_HEIGHT = 31;
const TOGGLE_PADDING = 2;
const TOGGLE_KNOB_SIZE = 27;
const TOGGLE_BORDER_RADIUS = TOGGLE_HEIGHT / 2;
const TOGGLE_KNOB_TRAVEL = TOGGLE_WIDTH - TOGGLE_PADDING * 2 - TOGGLE_KNOB_SIZE;

/**
 * Props for Toggle component
 */
export interface ToggleProps {
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when toggle state changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Accessible label for the toggle */
  'aria-label'?: string;
}

/**
 * Toggle component - an iOS-style switch for boolean settings
 *
 * Features:
 * - Consistent iOS-style appearance
 * - Theme-aware colors (uses CSS variables)
 * - Accessible with proper ARIA attributes
 * - Smooth animations
 * - Disabled state support
 *
 * @example
 * ```tsx
 * const [enabled, setEnabled] = useState(false);
 *
 * <Toggle
 *   checked={enabled}
 *   onChange={setEnabled}
 *   aria-label="Enable notifications"
 * />
 * ```
 */
export const Toggle = ({
  checked,
  onChange,
  disabled = false,
  'aria-label': ariaLabel,
}: ToggleProps) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={handleClick}
      disabled={disabled}
      style={{
        ...toggleContainerStyle,
        backgroundColor: checked
          ? 'var(--color-toggle-on, #34c759)'
          : 'var(--color-toggle-off, #e9e9eb)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          ...toggleKnobStyle,
          transform: checked ? `translateX(${TOGGLE_KNOB_TRAVEL}px)` : 'translateX(0)',
        }}
      />
    </button>
  );
};

/**
 * Toggle container styles
 */
const toggleContainerStyle: CSSProperties = {
  position: 'relative',
  width: `${TOGGLE_WIDTH}px`,
  height: `${TOGGLE_HEIGHT}px`,
  minWidth: `${TOGGLE_WIDTH}px`,
  minHeight: `${TOGGLE_HEIGHT}px`,
  maxHeight: `${TOGGLE_HEIGHT}px`,
  borderRadius: `${TOGGLE_BORDER_RADIUS}px`,
  border: 'none',
  padding: 0,
  transition: 'background-color 0.2s ease-out',
  boxSizing: 'border-box',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  overflow: 'hidden',
};

/**
 * Toggle knob styles
 */
const toggleKnobStyle: CSSProperties = {
  position: 'absolute',
  top: `${TOGGLE_PADDING}px`,
  left: `${TOGGLE_PADDING}px`,
  width: `${TOGGLE_KNOB_SIZE}px`,
  height: `${TOGGLE_KNOB_SIZE}px`,
  minWidth: `${TOGGLE_KNOB_SIZE}px`,
  minHeight: `${TOGGLE_KNOB_SIZE}px`,
  borderRadius: '50%',
  backgroundColor: 'white',
  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(0, 0, 0, 0.16)',
  transition: 'transform 0.2s ease-out',
  flexShrink: 0,
};

export default Toggle;
